"use client"
import IconMenu from "@/app/(site)/_components/Atoms/Icons/IconMenu"
import { MenuItemTypes } from "@/app/(site)/_mockdata/menuItems"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import React, { FC, useState } from "react"
import { DropdownVisibilityState } from ".."
import Text from "../../../Atoms/Text"
import DynamicLink from "../../../Molecules/Link"
import CountryPickerButton from "../CountryPickerButton"
import DropDownMeny from "./DropDown"

interface HeaderNavigationProps {
    menuItems: MenuItemTypes[]
    fallbackCentraSessionData: {
        countryCode?: string
        langCode?: string
    }
    locale: string
}

const Skeleton = () => {
    const skeleton =
        "w-full h-20 animate-pulseColor rounded bg-neutral-200 dark:bg-neutral-700"
    return (
        <div className="flex h-[56px] w-[220px] flex-row items-center gap-24">
            <div className={skeleton}></div>
            <div className={skeleton}></div>
            <div className={skeleton}></div>
        </div>
    )
}

const removeFifthLevelFunction = (items: MenuItemTypes[]): MenuItemTypes[] =>
    items
        .filter(item => item.level !== 5 && !item.hidden)
        .map(item => ({
            ...item,
            menuItems: item.menuItems ? removeFifthLevelFunction(item.menuItems) : [],
            children: item.menuItems ? false : true
        }))

const HeaderNavigation: FC<HeaderNavigationProps> = ({
    menuItems,
    fallbackCentraSessionData,
    locale
}) => {
    const [dropdownVisibility, setDropdownVisibility] = useState<DropdownVisibilityState>(
        {}
    )
    const showMobileMenu = useEcommerceStore(state => state.showMobileMenu)
    const setShowMobileMenu = useEcommerceStore(state => state.setShowMobileMenu)

    const handleMouseEnter = (menuName: string) => {
        setDropdownVisibility(prevState => ({ ...prevState, [menuName]: true }))
    }

    const handleMouseLeave = (menuName: string) => {
        setDropdownVisibility(prevState => ({ ...prevState, [menuName]: false }))
    }

    const removeFifthLevel = removeFifthLevelFunction(menuItems)

    // Accessibility keyboard interaction
    const handleKeyDown = (event: React.KeyboardEvent, item: MenuItemTypes) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            setDropdownVisibility(prevState => ({
                ...prevState,
                [item.title]: !prevState[item.title]
            }))
        }
        if (event.key === "Escape") {
            event.preventDefault()
            setDropdownVisibility(prevState => ({ ...prevState, [item.title]: false }))
        }
    }

    return (
        <>
            <ul className="hidden items-center pl-12 text-12 uppercase md:flex">
                {removeFifthLevel.length <= 0 && <Skeleton />}
                {removeFifthLevel
                    .filter(item => !item.hidden)
                    .map((item, index) => (
                        <li
                            key={index}
                            className="flex gap-8"
                            {...(item.title !== "Archive" &&
                                item.title !== "SALE TEST" &&
                                item.title !== "SALE" &&
                                item.title !== "Archive APAC" &&
                                item.title !== "Archive NA" && {
                                    onMouseEnter: () => handleMouseEnter(item.title),
                                    onMouseLeave: () => handleMouseLeave(item.title)
                                })}
                            onKeyDown={event => handleKeyDown(event, item)} // Handle keyboard interaction
                            aria-controls="dropdown-menu"
                        >
                            {dropdownVisibility[item.title]}
                            <DynamicLink
                                className="px-12 pb-24 pt-24"
                                href={item.uri}
                                onClick={() => setDropdownVisibility({})}
                                aria-expanded={dropdownVisibility[item.title]}
                            >
                                <Text asChild weight="regular" size="small">
                                    <span>{item.title}</span>
                                </Text>
                            </DynamicLink>
                            <DropDownMeny
                                showDropDownMenu={dropdownVisibility[item.title]}
                                setDropdownVisibility={setDropdownVisibility}
                                menuItems={item.menuItems ?? []}
                            />
                        </li>
                    ))}
            </ul>
            <div className="flex items-center gap-4 md:hidden">
                <div onClick={() => setShowMobileMenu(!showMobileMenu)}>
                    <div className="cursor-pointer py-16 pl-12 pr-14">
                        <IconMenu className="h-[22px] w-[22px]" />
                    </div>
                </div>
                <CountryPickerButton
                    fallbackCentraSessionData={fallbackCentraSessionData}
                    locale={locale}
                    mobile
                />
            </div>
        </>
    )
}

export default HeaderNavigation
